import MainModal from "components/MainModal";
import { useStyles } from "./AddRCAMembersModal.styles";
import { IAddRCAMembersModal } from "./AddRCAMembersModalInterfaces";
import { UsersEntity } from "entities/Users/UsersEntity";
import { RCA_TEAM_MEMBER_USER_TYPE_IDS } from "constants/constants";
import { IUser } from "entities/Users/UsersEntityInterfaces";
import {
    Autocomplete,
    Avatar,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

const AddRCAMembersModal = ({
    open,
    onClose,
    onSaveRCATeamMembers,
    currentRCATeamMembers,
    millId,
}: IAddRCAMembersModal) => {
    // Operators are included here but not in the other RCA pickers: they take part in the analysis
    // without owning it.
    const { loading, users } = UsersEntity.GetUsersByType(RCA_TEAM_MEMBER_USER_TYPE_IDS, millId);
    const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
    const { t } = useTranslation();
    const classes = useStyles();

    const currentRCATeamMembersParsed: IUser[] = useMemo(() => {
        return currentRCATeamMembers.map((member) => {
            return member.user;
        });
    }, [currentRCATeamMembers]);

    const usersParsed = useMemo(() => {
        return users.filter((user) => {
            return (
                !selectedUsers.find((selectedUser) => selectedUser.user_id === user.user_id) &&
                !currentRCATeamMembersParsed.find((currentMember) => currentMember.user_id === user.user_id)
            );
        });
    }, [currentRCATeamMembersParsed, selectedUsers, users]);

    return (
        <MainModal
            isOpen={open}
            onClose={onClose}
            title={t("rca.rca_library.rca_detail.rca_team_members_modal.title")}
            showCloseButton
            customModalStyles={{ width: "50%" }}
            okButton={t("rca.rca_library.rca_detail.rca_team_members_modal.select_users_and_continue")}
            onOkButtonClick={() => {
                onSaveRCATeamMembers(selectedUsers);
            }}
            disableOkButton={selectedUsers.length === 0}
        >
            <section className={classes.modalBodyWrapper}>
                <Autocomplete
                    className={classes.autocompleteWrapper}
                    id="tags-standard"
                    options={usersParsed}
                    fullWidth
                    getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                    renderOption={(props, option) => (
                        <li {...props} className={classes.listItemWrapper}>
                            <Avatar src={option.media?.media_url} />
                            <div className={classes.listItemTextWrapper}>
                                <span
                                    className={classes.listItemText}
                                >{`${option.first_name} ${option.last_name} (${option.user_type.name}) `}</span>
                                <span className={classes.listItemTextSubtitle}>
                                    {option.email || t("rca.rca_library.rca_detail.rca_team_members_modal.no_email")}
                                </span>
                            </div>
                        </li>
                    )}
                    onChange={(_, value) => {
                        if (!value) return;
                        setSelectedUsers([...selectedUsers, value as IUser]);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            placeholder={t("rca.rca_library.rca_detail.rca_team_members_modal.type_a_name")}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Divider className={classes.divider} />
                <List className={classes.listContainer}>
                    {selectedUsers.map((user, index) => {
                        return (
                            <div key={user.user_id} className={classes.separatorNoPadding}>
                                <ListItem key={index} className={classes.listItem}>
                                    <ListItemText
                                        className={classes.listText}
                                        primary={
                                            <div className={classes.listItemContainer}>
                                                <Avatar src={user.media?.media_url} />
                                                <div className={classes.listItemTextWrapper}>
                                                    <span
                                                        className={classes.listItemText}
                                                    >{`${user.first_name} ${user.last_name} (${user.user_type.name}) `}</span>
                                                    <span className={classes.listItemTextSubtitle}>
                                                        {user.email ||
                                                            t(
                                                                "rca.rca_library.rca_detail.rca_team_members_modal.no_email",
                                                            )}
                                                    </span>
                                                </div>
                                            </div>
                                        }
                                    />

                                    <IconButton
                                        onClick={() => {
                                            const newSelectedUsers = [...selectedUsers];
                                            newSelectedUsers.splice(index, 1);
                                            setSelectedUsers(newSelectedUsers);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItem>
                            </div>
                        );
                    })}
                </List>
            </section>
        </MainModal>
    );
};

export default AddRCAMembersModal;
